import {
    mdiAccountOutline,
    mdiAlert,
    mdiArrowULeftTop,
    mdiCalendarClock,
    mdiCheckCircle,
    mdiCheckCircleOutline,
    mdiCircleOutline,
    mdiClockOutline,
    mdiHeadset,
    mdiHelpCircleOutline,
    mdiLockOutline,
    mdiMinus,
    mdiPauseCircleOutline,
    mdiPlayCircleOutline,
    mdiRadioboxMarked,
    mdiRefresh,
    mdiSkipNext,
} from '@mdi/js';
import { Variables } from '../base';
import { EventOverlay, StageDisplayState, VisualState } from './claim-progress-derivation';

export type Tone = 'blue' | 'green' | 'amber' | 'red' | 'grey';

export interface IToneColors {
    background: string;
    foreground: string;
    border: string;
}

const T = Variables.ThemeLight;

/** Pale surface + dark foreground from the same ramp (NS-2): never a Mild/Medium pair. */
export const TONE_COLORS: Record<Tone, IToneColors> = {
    blue: { background: T.PrimaryMild2, foreground: T.PrimaryInverted3, border: T.PrimaryMedium1 },
    green: { background: T.SuccessMild3, foreground: T.SuccessInverted3, border: T.SuccessMedium1 },
    amber: { background: T.AttentionMild3, foreground: T.AttentionInverted3, border: T.AttentionMedium1 },
    red: { background: T.WarningMild3, foreground: T.WarningInverted3, border: T.WarningMedium1 },
    grey: { background: T.Background1, foreground: T.TextMedium, border: T.Border1 },
};

export interface IVisualStateView {
    label: string;
    icon: string;
    tone: Tone;
}

export const VISUAL_STATE_VIEW: Record<VisualState, IVisualStateView> = {
    ACTIVE: { label: 'Active', icon: mdiPlayCircleOutline, tone: 'blue' },
    AWAITING_TRANSMISSION_STATUS: { label: 'Awaiting transmission status', icon: mdiClockOutline, tone: 'blue' },
    AWAITING_PAYER: { label: 'Awaiting payer', icon: mdiClockOutline, tone: 'blue' },
    SCHEDULED: { label: 'Scheduled', icon: mdiCalendarClock, tone: 'grey' },
    PROVIDER_ACTION_NEEDED: { label: 'Provider action', icon: mdiAccountOutline, tone: 'amber' },
    ENTER_ACTION_NEEDED: { label: 'Enter action', icon: mdiHeadset, tone: 'amber' },
    SYSTEM_BLOCKED: { label: 'System blocked', icon: mdiLockOutline, tone: 'amber' },
    RETRYING: { label: 'Retrying', icon: mdiRefresh, tone: 'amber' },
    ACTION_REQUIRED_AFTER_RETRIES: { label: 'Action required', icon: mdiAlert, tone: 'red' },
    COMPLETED: { label: 'Completed', icon: mdiCheckCircleOutline, tone: 'green' },
    BYPASSED: { label: 'Bypassed', icon: mdiSkipNext, tone: 'grey' },
    UNCLASSIFIED: { label: 'Unclassified', icon: mdiHelpCircleOutline, tone: 'grey' },
};

export const OVERLAY_VIEW: Record<EventOverlay, { label: string; icon: string }> = {
    PAUSED: { label: 'Paused', icon: mdiPauseCircleOutline },
    REWORK: { label: 'Rework', icon: mdiArrowULeftTop },
};

export interface IStageVisual {
    tone: Tone | 'pending';
    icon: string;
    caption: string;
}

export const STAGE_VISUAL: Record<StageDisplayState, IStageVisual> = {
    COMPLETE: { tone: 'green', icon: mdiCheckCircle, caption: 'Complete' },
    COMPLETE_REWORK_ACTIVE: { tone: 'amber', icon: mdiArrowULeftTop, caption: 'Complete · rework' },
    ACTIVE: { tone: 'blue', icon: mdiRadioboxMarked, caption: 'Active' },
    BLOCKED: { tone: 'red', icon: mdiLockOutline, caption: 'Blocked' },
    PENDING: { tone: 'pending', icon: mdiCircleOutline, caption: 'Pending' },
    NOT_APPLICABLE: { tone: 'pending', icon: mdiMinus, caption: 'Not needed' },
    UNKNOWN: { tone: 'pending', icon: mdiHelpCircleOutline, caption: 'Unknown' },
};
